<template>
  <div class="models-page" v-if="!serversLoading">
    <div v-for="(model_config, key) in configs" :key="key">
      <settings-layout
        v-model="configs[key]"
        :items="configs[key]"
        :isAdmin="isAdmin"
        :loading="configs[key].serversLoading"
        :properties="properties[key]"
        :status="configs[key].status"
        :img="configs[key].modelImg"
        :imgLoading="configs[key].modelImgLoading"
        @click:save="save(key)"
      >
        <template #test>{{ '' }}</template>
        <template #type>
          {{ configs[key].name }}
        </template>
        <template #actions v-if="configs[key].model_id">
          <b-dropdown-item @click="openDialogRemove(key)" aria-role="listitem">{{
            $t('server.model.remove')
          }}</b-dropdown-item>
          <b-dropdown-item @click="openDialogReset(key)" aria-role="listitem">{{
            $t('server.model.reset')
          }}</b-dropdown-item>
        </template>
        <template #upload v-if="configs[key].model_id">
          <input type="file" :ref="`file`" style="display: none" @change="saveImage(key)" />
          <a class="img-uplod-link" @click="$refs[`file`][key].click()">{{ $t('server.model.upload_image_sample') }}</a>
        </template>
      </settings-layout>
      <hr />
    </div>
    <edit-button
      class="add-model"
      v-if="availableModelTypes.length"
      :editOptions="['add_model']"
      @click="isModalActive = true"
    >
      <template>
        <span class="text">{{ $t('button.add_model') }}</span>
      </template>
      <template #description>
        <span class="add-model-text">{{ note_text }}</span>
      </template>
    </edit-button>

    <modal-layout
      :active="isModalActive"
      :items="availableModelTypes"
      field="type"
      field_description="default_description"
      @click:load="load"
      @close="isModalActive = false"
    ></modal-layout>
  </div>
  <loader v-else></loader>
</template>

<script>
import { createNamespacedHelpers, mapActions } from 'vuex'
import SettingsLayout from '@/layout/SettingsLayout'
import ModalLayout from '@/layout/ModalLayout'

import EditButton from '@/components/EditButton'
import Loader from '@/components/Loader'

const { mapState: mapUserState } = createNamespacedHelpers('general')

export default {
  name: 'Source',
  components: {
    Loader,
    EditButton,
    ModalLayout,
    SettingsLayout
  },
  computed: {
    ...mapUserState({
      role: state => state.user.role,
      title: state => state.title
    }),
    isAdmin() {
      return this.role === 'ADMIN'
    }
  },
  data() {
    return {
      isModalActive: false,
      serversLoading: false,
      configs: [],
      properties: [],
      status: '',
      modelImg: '',
      availableModelTypes: [],
      note_text: ''
    }
  },
  created() {
    if (!this.isAdmin) {
      this.$router.push({
        name: 'sources',
        params: {
          server_id: this.$route.params.server_id
        }
      })
    }
    this.getDefaultConfig()
  },
  methods: {
    ...mapActions('general', ['setTitle']),
    ...mapActions('general', ['setServerName']),
    load(data) {
      this.isModalActive = false
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/models/load`
      fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.params.server_id,
          model_type: data.type
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.getDefaultConfig()
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
    },
    saveImage(key) {
      const form = new FormData()
      form.append('file', this.$refs[`file`][key].files[0])
      form.append('server_id', this.$route.params.server_id)
      form.append('model_id', this.configs[key].model_id)
      const requestOptions = {
        credentials: 'include',
        method: 'POST',
        body: form
      }

      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/models/image`
      return fetch(url, requestOptions)
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.getImage(key)
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
    },
    save(key) {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/models`
      let model_config = Object.assign({}, this.configs[key])
      delete model_config.modelImg
      delete model_config.modelImgLoading
      delete model_config.serversLoading
      this.configs[key].serversLoading = true
      fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.params.server_id,
          model_config: model_config
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.$notify({
            message: `${this.$t('notification.saved', {
              title: this.configs[key].name
            })}.`,
            type: 'success'
          })
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.configs[key].serversLoading = false
          this.$forceUpdate()
        })
    },
    resetModel(key) {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/models/reset`
      this.serversLoading = true
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.params.server_id,
          model_id: this.configs[key].model_id
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(data => {
          this.configs = data.config
          this.properties = data.properties
          this.note_text = data.note_text
          for (let i = 0; i < this.configs.length; ++i) {
            this.getImage(i)
            this.configs[i].serversLoading = false
          }
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.serversLoading = false
        })
    },
    getDefaultConfig() {
      if (!this.serversLoading) {
        if (this.$route.name === 'add_models' && this.$route.query.duplicate != undefined) {
          return this.duplicate()
        }

        const body = {
          server_id: this.$route.params.server_id
        }

        const searchParams = new URLSearchParams(body)
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/models?${searchParams}`
        this.serversLoading = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            this.configs = data.config
            this.properties = data.properties
            this.note_text = data.note_text
            this.availableModelTypes = data.available_model_types
            this.setServerName(data.server_name)
            for (let i = 0; i < this.configs.length; ++i) {
              this.getImage(i)
              this.configs[i].serversLoading = false
            }
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.serversLoading = false
          })
      }
    },
    getImage(index) {
      const body = {
        server_id: this.$route.params.server_id,
        model_id: this.configs[index].model_id
      }

      const searchParams = new URLSearchParams(body)
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/models/image?${searchParams}`
      this.configs[index].modelImgLoading = true
      return fetch(url, {
        method: 'GET',
        credentials: 'include'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(modelImg => {
          this.configs[index].modelImg = modelImg
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.configs[index].modelImgLoading = false
          this.$forceUpdate()
        })
    },
    deleteModel(key) {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/models/delete`
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.params.server_id,
          model_id: this.configs[key].model_id
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.configs.splice(key, 1)
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
    },
    openDialogRemove(key) {
      this.$dialog.confirm({
        message: `${this.$t('confirm.remove', {
          msg: this.configs[key].name
        })}?`,
        onConfirm: () => {
          this.configs[key].serversLoading = true
          this.deleteModel(key)
            .then(() => {
              this.$notify({
                message: this.$t('notification.removed', {
                  msg: this.configs[key].name
                }),
                type: 'success'
              })
            })
            .finally(() => {
              this.configs[key].serversLoading = false
            })
        }
      })
    },
    openDialogReset(key) {
      this.$dialog.confirm({
        message: `${this.$t('confirm.reset', {
          msg: this.configs[key].name
        })}?`,
        onConfirm: () => {
          this.configs[key].serversLoading = true
          this.resetModel(key)
            .then(() => {
              this.$notify({
                message: this.$t('notification.reset', {
                  msg: this.configs[key].name
                }),
                type: 'success'
              })
            })
            .finally(() => {
              this.configs[key].serversLoading = false
            })
        }
      })
    }
  }
}
</script>

<style lang="scss">
.models-page {
  hr {
    background-color: #c3c3c3;
  }
  .img-uplod-link {
    text-align: right;
    width: 100%;
    display: block;
  }
  .add-model {
    .add-model-text {
      margin-left: 1rem;
    }
  }
}
</style>
